// libs
import React, { memo, useEffect, useCallback, useRef, useMemo } from 'react';

// context
import TrackingContext from './TrackingContext';

// interface
import { WidgetTrackingData } from 'shared/components/AdPlatformWidgets/types';

// helpers
import { sendEvents, addEvents, logData, setCommonConfig, sendClickEvent } from './eventHelpers';

// analytics
import { onClickSendEvent, onImpressionEvent } from './analytics';

// defs
import { ITrackingCommonFields } from './types';

interface IImpressionTrackingProps
  extends Pick<ITrackingCommonFields, 'platform' | 'source' | 'vertical'> {
  children: React.ReactNode;
  eventApiInterval: number;
  enabled: boolean;
  customerId: string;
}

function ImpressionTracking({
  children,
  platform,
  source,
  vertical,
  eventApiInterval,
  enabled,
  customerId,
}: IImpressionTrackingProps) {
  const observer = useRef<IntersectionObserver | null>(null);
  const dataMap = useRef<Map<Element, WidgetTrackingData>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCommonConfig({ platform, source, vertical, customer_id: customerId });
  }, [platform, source, vertical, customerId]);

  useEffect(() => {
    window.addEventListener('unload', logData);
    return () => {
      window.removeEventListener('unload', logData);
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      sendEvents();
    }, eventApiInterval);

    return () => {
      window.clearInterval(interval);
    };
  }, [eventApiInterval]);

  const updateEvents = useCallback((element: Element) => {
    const trackingData = dataMap.current.get(element);
    if (trackingData === undefined) {
      return;
    }

    onImpressionEvent({ ...trackingData });
    addEvents({ ...trackingData, event: 'adplatform:impressions' });
  }, []);

  const onWidgetClick = useCallback((trackingData: WidgetTrackingData) => {
    onClickSendEvent({ ...trackingData });
    sendClickEvent({ ...trackingData, event: 'adplatform:click' });
  }, []);

  const onIntersect: IntersectionObserverCallback = useCallback(
    (e) => {
      e.forEach((entry) => {
        if (entry.isIntersecting) {
          updateEvents(entry.target);
        }
      });
    },
    [updateEvents]
  );

  const attachObserver = useCallback((ref: HTMLElement, trackingData: WidgetTrackingData) => {
    if (observer.current) {
      observer.current.observe(ref);
    }
    if (dataMap.current.has(ref) === false) {
      dataMap.current.set(ref, trackingData);
    }
  }, []);

  const detachObserver = useCallback((ref: HTMLElement) => {
    if (observer.current) {
      observer.current.unobserve(ref);
    }
    if (dataMap.current.has(ref)) {
      dataMap.current.delete(ref);
    }
  }, []);

  useEffect(() => {
    observer.current = new IntersectionObserver(onIntersect);
    return () => {
      if (observer.current !== null) {
        observer.current.disconnect();
      }
    };
  }, [onIntersect]);

  const values = useMemo(
    () => ({ attachObserver, detachObserver, onWidgetClick }),
    [attachObserver, detachObserver, onWidgetClick]
  );

  if (enabled === false) {
    return <>{children}</>;
  }

  return (
    <div ref={containerRef}>
      <TrackingContext.Provider value={values}>{children}</TrackingContext.Provider>
    </div>
  );
}

export default memo(ImpressionTracking);
